import { useEffect, useState } from "react";
import { apiFetch } from "../../../context/AuthContext";
import { API_URL } from "../config";
import { flattenHierarchy } from "../utils/hierarchyUtils";
import { mapEmployee } from "../utils/employeeMapper";
import { getCached } from "../utils/employeeCache";

// Employees list + hierarchy expand/collapse state
export const useEmployees = ({ profile, language, text, setLoading, setError }) => {
  // Employee data state
  const [employees, setEmployees] = useState([]);

  const [expandedEmployees, setExpandedEmployees] = useState(() => {
    const myCode = localStorage.getItem("infinityCode");
    return myCode ? new Set([myCode]) : new Set();
  });

  // Toggle expand/collapse for a hierarchy node
  const toggleEmployeeExpand = (infinityCode) => {
    setExpandedEmployees(prev => {
      const newSet = new Set(prev);
      if (newSet.has(infinityCode)) {
        newSet.delete(infinityCode);
      } else {
        newSet.add(infinityCode);
      }
      return newSet;
    });
  };

  // Fetches the raw employees/hierarchy payload, cached per client+manager.
  // Expanding/collapsing a hierarchy node only changes how the *same* data
  // gets flattened for display — it never needs a fresh network round-trip,
  // so that re-flatten step is kept out of this cached fetch entirely.
  const fetchRawEmployees = (clientName, myInfinityCode, forceRefresh) => {
    const cacheKey = `manager-employees:${clientName || ""}:${myInfinityCode || ""}`;
    return getCached(
      cacheKey,
      async () => {
        let response;
        if (clientName && clientName.toLowerCase() !== "infinity") {
          console.log(`📡 Fetching hierarchy for client: ${clientName}, manager: ${myInfinityCode}`);
          response = await apiFetch(
            `${API_URL}/employees/clients/${clientName}/hierarchy?managerInfinityCode=${myInfinityCode}`,
            { method: "GET" }
          );
        } else {
          console.log("📡 Fetching all employees");
          response = await apiFetch(`${API_URL}/employees`, { method: "GET" });
        }
        return response.json();
      },
      { force: forceRefresh }
    );
  };

  // `forceRefresh` bypasses the cache — pass true after a mutation
  // (add/edit/delete/archive/restore) so this never renders stale data.
  // Otherwise a fresh-enough cached payload is served instantly, which is
  // what makes toggling hierarchy nodes (and revisiting this page) fast
  // instead of re-hitting the network every single time.
  const fetchEmployees = async (forceRefresh = false) => {
    try {
      setLoading(true);

      const clientName = profile?.clientName || localStorage.getItem("clientName");
      const myInfinityCode = profile?.infinityCode || localStorage.getItem("infinityCode");

      const json = await fetchRawEmployees(clientName, myInfinityCode, forceRefresh);

      let employeesData;

      // Check if response contains hierarchy structure
      if (json.hierarchy) {
        // Flatten the hierarchy for table display
        employeesData = flattenHierarchy(json.hierarchy[0], expandedEmployees);
      } else {
        employeesData = json.employees || json;
      }

      console.log(`✅ Received ${employeesData.length} employees`);

      // Map employees with ALL fields
      const mappedEmployees = employeesData.map((emp) => mapEmployee(emp, language, text));

      setEmployees(mappedEmployees);
      console.log(`✅ Successfully mapped ${mappedEmployees.length} employees`);
    } catch (err) {
      console.error("❌ Failed to fetch employees:", err);
      setError("Failed to fetch employees data");
    } finally {
      setLoading(false);
    }
  };

  // Re-flatten when expandedEmployees changes — thanks to fetchRawEmployees'
  // cache, this reuses the already-fetched hierarchy instead of refetching.
  useEffect(() => {
    if (employees.length > 0) {
      fetchEmployees();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedEmployees]);

  return { employees, setEmployees, expandedEmployees, toggleEmployeeExpand, fetchEmployees };
};
